import { useEffect, useMemo, useRef, useState } from "react";
import { Patient } from "../../../types/patient";
import AuthService from "../../services/AuthService";
import {
  createPatient,
  listPatients,
  updatePatient,
  deletePatient,
} from "../../services/patientApi";
import { getPatientMoleMappings } from "../../services/moleMappingApi";
import { Box, Stack } from "@mui/system";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
  CircularProgress,
  Collapse,
  Chip,
  Grid,
  alpha,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Avatar,
  Paper,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
} from "@mui/material";
import PatientFormDialog from "./PatientFormDialog";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MapIcon from "@mui/icons-material/Map";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import WcIcon from "@mui/icons-material/Wc";
import FolderIcon from "@mui/icons-material/Folder";
import React from "react";
import { useNavigate } from "react-router-dom";
import TourGuide from "../../../features/components/TourGuide";
import SchoolIcon from "@mui/icons-material/School";
import CloseIcon from '@mui/icons-material/Close';
import {
  People,
  PersonAdd,
  Timeline,
  Assessment, CallEnd as Calendar, // Fixed: Changed from CallEnd to Calendar
  DataUsage,
  Groups,
} from "@mui/icons-material";

const sexes = ["female", "male", "other", "unknown"];

interface MoleSession {
  id: string;
  sessionDate: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export default function PatientSpace() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [patients, setPatients] = useState<Patient[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  
  // Add state for tour guide
  const [runTour, setRunTour] = useState(false);
  
  // Add state for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  
  // Mobile drawer state
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Session states
  const [expandedPatients, setExpandedPatients] = useState<Set<string>>(new Set());
  const [patientSessions, setPatientSessions] = useState<Record<string, MoleSession[]>>({});
  const [loadingSessions, setLoadingSessions] = useState<Set<string>>(new Set());

  // Session edit states
  const [editSessionOpen, setEditSessionOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<MoleSession | null>(null);
  const [sessionForm, setSessionForm] = useState({
    sessionDate: "",
    notes: "",
  });
  const [sessionErrors, setSessionErrors] = useState({
    sessionDate: "",
    general: "",
  });

  // edit state
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Patient | null>(null);
  const [editForm, setEditForm] = useState({
    givenName: "",
    familyName: "",
    dateOfBirth: "",
    sex: "",
    notes: "",
  });
  const [editErrors, setEditErrors] = useState({
    givenName: "",
    familyName: "",
    general: "",
  });

  const authService = new AuthService();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const [hasMore, setHasMore] = useState(true);
  
  function mergeUniqueById<T extends { id: string }>(
    existing: T[],
    incoming: T[]
  ) {
    const map = new Map(existing.map((x) => [x.id, x]));
    for (const x of incoming) map.set(x.id, x); // incoming overwrites existing
    return Array.from(map.values());
  }

  // fetch patients (with search, load, reset)
// fetch patients (with search, load, reset)
async function fetchPatients(search?: string, reset = false) {
  setLoading(true);
  try {
    const token = await authService.getAccessToken();
    if (!token) throw new Error("No access token available");
    
    // If doing a search or reset, we need to start from offset 0
    if (reset || search !== undefined) {
      setOffset(0);
      // Use the provided search term, or empty string if reset with no search
      const searchTerm = search !== undefined ? search : (reset ? "" : q);
      const data = await listPatients(token, searchTerm || undefined, limit, 0);
      setPatients(data);
      setHasMore(data.length === limit);
      setOffset(data.length);
    } else {
      // Normal pagination - load more with current search query (q)
      const data = await listPatients(token, q || undefined, limit, offset);
      setPatients(prev => mergeUniqueById(prev, data));
      setHasMore(data.length === limit);
      setOffset(offset + data.length);
    }
  } catch (error) {
    console.error("Failed to fetch patients:", error);
  } finally {
    setLoading(false);
  }
}


  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return; // prevents the second StrictMode call
    didInit.current = true;
    fetchPatients(undefined, /*reset=*/ true);
  }, []);
  
  const rows = useMemo(() => patients, [patients]);

  // Copy ID to clipboard
  function handleCopyId(id: string, type: 'patient' | 'session') {
    navigator.clipboard.writeText(id);
    // Could add a snackbar notification here
  }

  // Calculate age from date of birth
  function calculateAge(dob: string): string | null {
    if (!dob) return null;
    const years = new Date().getFullYear() - new Date(dob).getFullYear();
    return `${years}y`;
  }

  async function handleCreate(data: {
    givenName: string;
    familyName: string;
    dateOfBirth?: string | null;
    sex?: string | null;
    notes?: string | null;
  }) {
    const token = await authService.getAccessToken();
    if (!token) throw new Error("No access token available");
    
    // Validate required fields
    if (!data.dateOfBirth) {
      throw new Error("Date of birth is required");
    }
    
    // Check for duplicate patient names
    const trimmedGivenName = data.givenName.trim().toLowerCase();
    const trimmedFamilyName = data.familyName.trim().toLowerCase();
    
    const existingPatient = patients.find(p => 
      p.givenName?.trim().toLowerCase() === trimmedGivenName && 
      p.familyName?.trim().toLowerCase() === trimmedFamilyName
    );
    
    if (existingPatient) {
      throw new Error(`A patient with the name "${data.givenName.trim()} ${data.familyName.trim()}" already exists`);
    }
    
    const created = await createPatient(token, {
      givenName: data.givenName,
      familyName: data.familyName,
      dateOfBirth: data.dateOfBirth, // Keep the format as received from the form
      sex: data.sex ?? undefined,
      notes: data.notes ?? undefined,
    });
    
    // Reset and reload patient data from scratch
    setOffset(0);
    await fetchPatients(undefined, /*reset=*/true);
  }

  function openEditDialog(p: Patient) {
    setEditing(p);
    setEditForm({
      givenName: p.givenName || "",
      familyName: p.familyName || "",
      dateOfBirth: p.dateOfBirth
        ? dayjs(p.dateOfBirth).format("YYYY-MM-DD")
        : "",
      sex: p.sex || "",
      notes: p.notes || "",
    });
    setEditOpen(true);
  }

  async function handleUpdate() {
    if (!editing) return;
    
    // Clear previous errors
    setEditErrors({
      givenName: "",
      familyName: "",
      general: "",
    });

    // Validate required fields
    const newErrors = {
      givenName: "",
      familyName: "",
      general: "",
    };

    if (!editForm.givenName.trim()) {
      newErrors.givenName = "Given name is required";
    }

    if (!editForm.familyName.trim()) {
      newErrors.familyName = "Family name is required";
    }

    if (newErrors.givenName || newErrors.familyName) {
      setEditErrors(newErrors);
      return;
    }

    try {
      const token = await authService.getAccessToken();
      if (!token) throw new Error("No access token available");
      
      // Check for duplicate patient names (excluding current patient)
      const trimmedGivenName = editForm.givenName.trim().toLowerCase();
      const trimmedFamilyName = editForm.familyName.trim().toLowerCase();
      
      const existingPatient = patients.find(p => 
        p.id !== editing.id && // Exclude current patient
        p.givenName?.trim().toLowerCase() === trimmedGivenName && 
        p.familyName?.trim().toLowerCase() === trimmedFamilyName
      );
      
      if (existingPatient) {
        setEditErrors({
          givenName: "",
          familyName: "",
          general: `A patient with the name "${editForm.givenName.trim()} ${editForm.familyName.trim()}" already exists`,
        });
        return;
      }

      const body = {
        givenName: editForm.givenName.trim(),
        familyName: editForm.familyName.trim(),
        dateOfBirth: editForm.dateOfBirth
          ? new Date(editForm.dateOfBirth).toISOString()
          : null,
        sex: editForm.sex || null,
        notes: editForm.notes || null,
      };
      const updated = await updatePatient(token, editing.id, body);
      // update local state
      setPatients((prev) => prev.map((p) => (p.id === editing.id ? updated : p)));
      setEditOpen(false);
      setEditing(null);
      setEditErrors({
        givenName: "",
        familyName: "",
        general: "",
      });
    } catch (error) {
      console.error("Failed to update patient:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      setEditErrors({
        givenName: "",
        familyName: "",
        general: errorMessage,
      });
    }
  }

  function openDeleteDialog(patient: Patient) {
    setPatientToDelete(patient);
    setDeleteDialogOpen(true);
  }

  async function handleDelete(id: string) {
    const token = await authService.getAccessToken();
    if (!token) throw new Error("No access token available");
    await deletePatient(token, id);
    setPatients((prev) => prev.filter((p) => p.id !== id));
    setDeleteDialogOpen(false);
    setPatientToDelete(null);
  }

  // Navigate to mole mapping for a specific patient
  function goToMoleMapping(patientId: string) {
    navigate(`/molemapping/${patientId}`);
  }
  
  // Mobile drawer handlers
  const openActionSheet = (patient: Patient) => {
    setSelectedPatient(patient);
    setActionSheetOpen(true);
  };
  
  const closeActionSheet = () => {
    setActionSheetOpen(false);
  };
  
  const handleActionAndClose = (action: () => void) => {
    action();
    closeActionSheet();
  };

  // Define tour steps for the patient space
  const tourSteps = useMemo(() => {
    const baseSteps = [
      {
        target: '[data-tour="patient-search"]',
        content: patients.length
          ? 'Search for patients by name or patient ID here'
          : 'Once you have patients, you can search them here by name or patient ID',
        disableBeacon: true,
      },
      {
        target: '[data-tour="new-patient"]',
        content: patients.length
          ? 'Create a new patient record with this button'
          : 'Start by creating your first patient record',
        disableBeacon: true,
      },
      ...(patients.length
        ? [
            {
              target: '[data-tour="patient-actions"]',
              content:
                'Create mole mapping sessions (🗺️), edit patient details, or delete patient records',
              disableBeacon: true,
            },
          ]
        : []),
    ];
    return baseSteps;
  }, [patients]);

  const handleStartTour = (e: React.MouseEvent) => {
    e.preventDefault();
    // Reset the tour completion status in localStorage
    localStorage.removeItem("tour_patient-space_completed");
    localStorage.removeItem("tour_patient-space_step");
    // Force tour to stop and then start again (important for restarting)
    setRunTour(false);
    // Use setTimeout to ensure React has time to process the false state before setting to true
    setTimeout(() => setRunTour(true), 50);
  };

  const handleFinishTour = () => {
    setRunTour(false);
  };

  // Fetch patient sessions
  async function fetchPatientSessions(patientId: string) {
    if (patientSessions[patientId]) return; // Already loaded
    
    setLoadingSessions(prev => new Set(prev).add(patientId));
    try {
      const sessions = await getPatientMoleMappings(patientId);
      setPatientSessions(prev => ({
        ...prev,
        [patientId]: sessions || []
      }));
    } catch (error) {
      console.error("Failed to fetch patient sessions:", error);
      setPatientSessions(prev => ({
        ...prev,
        [patientId]: []
      }));
    } finally {
      setLoadingSessions(prev => {
        const newSet = new Set(prev);
        newSet.delete(patientId);
        return newSet;
      });
    }
  }

  // Toggle patient expansion
  function togglePatientExpansion(patientId: string) {
    const isExpanded = expandedPatients.has(patientId);
    const newExpanded = new Set(expandedPatients);
    
    if (isExpanded) {
      newExpanded.delete(patientId);
    } else {
      newExpanded.add(patientId);
      fetchPatientSessions(patientId);
    }
    
    setExpandedPatients(newExpanded);
  }

  // Open session edit dialog
  function openSessionEditDialog(session: MoleSession) {
    setEditingSession(session);
    setSessionForm({
      sessionDate: dayjs(session.sessionDate).format("YYYY-MM-DD"),
      notes: session.notes || "",
    });
    setEditSessionOpen(true);
  }

  // Handle session update
  async function handleUpdateSession() {
    if (!editingSession) return;
    
    setSessionErrors({
      sessionDate: "",
      general: "",
    });

    if (!sessionForm.sessionDate) {
      setSessionErrors(prev => ({
        ...prev,
        sessionDate: "Session date is required"
      }));
      return;
    }

    try {
      // Here you would call your update session API
      // For now, we'll just update the local state
      const updatedSession = {
        ...editingSession,
        sessionDate: new Date(sessionForm.sessionDate).toISOString(),
        notes: sessionForm.notes,
        updatedAt: new Date().toISOString(),
      };

      // Update the session in the patient sessions
      setPatientSessions(prev => {
        const patientId = Object.keys(prev).find(pid => 
          prev[pid].some(s => s.id === editingSession.id)
        );
        
        if (patientId) {
          return {
            ...prev,
            [patientId]: prev[patientId].map(s => 
              s.id === editingSession.id ? updatedSession : s
            )
          };
        }
        return prev;
      });

      setEditSessionOpen(false);
      setEditingSession(null);
    } catch (error) {
      console.error("Failed to update session:", error);
      setSessionErrors(prev => ({
        ...prev,
        general: "Failed to update session. Please try again."
      }));
    }
  }

  // Calculate statistics
  const patientStats = useMemo(() => {
    const totalPatients = patients.length;
    const maleCount = patients.filter(p => p.sex === 'male').length;
    const femaleCount = patients.filter(p => p.sex === 'female').length;
    const otherCount = patients.filter(p => p.sex === 'other').length;
    const unknownCount = patients.filter(p => !p.sex || p.sex === 'unknown').length;
    
    // Age distribution
    const currentDate = new Date();
    const ageGroups = {
      '0-18': 0,
      '19-35': 0,
      '36-50': 0,
      '51-65': 0,
      '65+': 0,
      'unknown': 0
    };
    
    patients.forEach(p => {
      if (p.dateOfBirth) {
        const age = currentDate.getFullYear() - new Date(p.dateOfBirth).getFullYear();
        if (age <= 18) ageGroups['0-18']++;
        else if (age <= 35) ageGroups['19-35']++;
        else if (age <= 50) ageGroups['36-50']++;
        else if (age <= 65) ageGroups['51-65']++;
        else ageGroups['65+']++;
      } else {
        ageGroups['unknown']++;
      }
    });
    
    // Recent activity
    const recentPatients = patients.filter(p => 
      p.createdAt && dayjs().diff(dayjs(p.createdAt), 'days') <= 7
    ).length;
    
    // Total sessions across all patients
    const totalSessions = Object.values(patientSessions).reduce((sum, sessions) => sum + sessions.length, 0);
    
    return {
      totalPatients,
      maleCount,
      femaleCount,
      otherCount,
      unknownCount,
      ageGroups,
      recentPatients,
      totalSessions
    };
  }, [patients, patientSessions]);

  return (
    <Box sx={{ 
      p: { xs: 1, md: 2 },
      background: isDarkMode 
        ? 'radial-gradient(circle at top right, rgba(35,35,40,1) 0%, rgba(28,28,33,1) 100%)'
        : 'radial-gradient(circle at top right, rgba(255,255,255,1) 0%, rgba(240,249,255,1) 100%)',
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Background pattern */}
      <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          opacity: isDarkMode ? 0.05 : 0.03,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%2303a9f4" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3Ccircle cx="15" cy="15" r="1"/%3E%3Ccircle cx="45" cy="15" r="1"/%3E%3Ccircle cx="15" cy="45" r="1"/%3E%3Ccircle cx="45" cy="45" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          backgroundSize: '40px 40px',
          zIndex: 0,
        }}></Box>

      {/* Statistics Overview */}
      <Paper elevation={4} sx={{ 
        mb: 3, 
        p: { xs: 2, sm: 3 },
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(40,30,60,1) 0%, rgba(30,25,50,1) 100%)'
          : 'linear-gradient(135deg, rgba(250,245,255,1) 0%, rgba(245,240,255,1) 100%)',
        borderRadius: 3,
        position: 'relative',
        zIndex: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Groups color="primary" sx={{ mr: 1, fontSize: '2rem' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Patient Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your patient demographics and activity summary
            </Typography>
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          {/* Total Patients */}
          <Grid item xs={6} sm={3}>
            <Card elevation={2} sx={{ 
              p: 2, 
              textAlign: 'center',
              background: isDarkMode 
                ? alpha(theme.palette.background.paper, 0.8) 
                : alpha(theme.palette.common.white, 0.9),
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <People color="primary" sx={{ fontSize: '2.5rem', mb: 1, mx: 'auto' }} />
              <Typography variant="h3" color="primary" fontWeight="bold">
                {patientStats.totalPatients}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Patients
              </Typography>
            </Card>
          </Grid>
          
          {/* Recent Patients */}
          <Grid item xs={6} sm={3}>
            <Card elevation={2} sx={{ 
              p: 2, 
              textAlign: 'center',
              background: isDarkMode 
                ? alpha(theme.palette.background.paper, 0.8) 
                : alpha(theme.palette.common.white, 0.9),
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <PersonAdd color="success" sx={{ fontSize: '2.5rem', mb: 1, mx: 'auto' }} />
              <Typography variant="h3" color="success.main" fontWeight="bold">
                {patientStats.recentPatients}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                New This Week
              </Typography>
            </Card>
          </Grid>
          
          {/* Total Sessions */}
          <Grid item xs={6} sm={3}>
            <Card elevation={2} sx={{ 
              p: 2, 
              textAlign: 'center',
              background: isDarkMode 
                ? alpha(theme.palette.background.paper, 0.8) 
                : alpha(theme.palette.common.white, 0.9),
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Assessment color="info" sx={{ fontSize: '2.5rem', mb: 1, mx: 'auto' }} />
              <Typography variant="h3" color="info.main" fontWeight="bold">
                {patientStats.totalSessions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mapping Sessions
              </Typography>
            </Card>
          </Grid>
          
          {/* Average Age */}
          <Grid item xs={6} sm={3}>
            <Card elevation={2} sx={{ 
              p: 2, 
              textAlign: 'center',
              background: isDarkMode 
                ? alpha(theme.palette.background.paper, 0.8) 
                : alpha(theme.palette.common.white, 0.9),
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Calendar color="warning" sx={{ fontSize: '2.5rem', mb: 1, mx: 'auto' }} />
              <Typography variant="h3" color="warning.main" fontWeight="bold">
                {patients.filter(p => p.dateOfBirth).length > 0 
                  ? Math.round(patients.filter(p => p.dateOfBirth).reduce((sum, p) => {
                      const age = new Date().getFullYear() - new Date(p.dateOfBirth!).getFullYear();
                      return sum + age;
                    }, 0) / patients.filter(p => p.dateOfBirth).length)
                  : '—'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Age
              </Typography>
            </Card>
          </Grid>

          {/* Gender Distribution */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.15) : alpha(theme.palette.common.white, 0.6),
              height: '100%'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DataUsage color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight="medium">
                  Gender Distribution
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Female</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={patientStats.totalPatients > 0 ? (patientStats.femaleCount / patientStats.totalPatients) * 100 : 0}
                      color="secondary"
                      sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {patientStats.femaleCount} ({patientStats.totalPatients > 0 ? Math.round((patientStats.femaleCount / patientStats.totalPatients) * 100) : 0}%)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Male</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={patientStats.totalPatients > 0 ? (patientStats.maleCount / patientStats.totalPatients) * 100 : 0}
                      color="primary"
                      sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {patientStats.maleCount} ({patientStats.totalPatients > 0 ? Math.round((patientStats.maleCount / patientStats.totalPatients) * 100) : 0}%)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Age Groups */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.15) : alpha(theme.palette.common.white, 0.6),
              height: '100%'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Timeline color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight="medium">
                  Age Distribution
                </Typography>
              </Box>
              <Grid container spacing={1}>
                {Object.entries(patientStats.ageGroups).filter(([key]) => key !== 'unknown').map(([ageGroup, count]) => (
                  <Grid item xs={6} key={ageGroup}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.1) : alpha(theme.palette.common.white, 0.7),
                      borderRadius: 1,
                      p: 1,
                      mb: 0.5
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {ageGroup}
                      </Typography>
                      <Chip 
                        label={count}
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Paper elevation={4} sx={{ 
        mb: 3, 
        p: { xs: 2, sm: 3 },
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(30,80,130,1) 0%, rgba(20,60,110,1) 100%)'
          : 'linear-gradient(135deg, rgba(25,118,210,1) 0%, rgba(30,136,229,1) 100%)',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        color: 'white',
        zIndex: 1,
      }}>
        {/* Background pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          opacity: 0.1,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3Ccircle cx="15" cy="15" r="2"/%3E%3Ccircle cx="45" cy="45" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          backgroundSize: '40px 40px',
          zIndex: 0,
        }} />
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            Quick Actions
          </Typography>
          <Typography variant="body2" sx={{ 
            color: alpha('#ffffff', 0.9),
            mb: 3
          }}>
            Get started with patient management
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                size="large"
                startIcon={<PersonAdd />}
                onClick={() => setOpen(true)}
                fullWidth
                sx={{ 
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'rgba(255,255,255,0.95)',
                  color: theme.palette.primary.main,
                  borderRadius: 2,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  '&:hover': {
                    background: 'rgba(255,255,255,1)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                  },
                  transition: 'all 0.3s ease',
                }}
                data-tour="new-patient"
              >
                Create New Patient
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<SchoolIcon />}
                onClick={handleStartTour}
                fullWidth
                sx={{ 
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Start Guided Tour
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Main Patient Table Card */}
      <Card elevation={2} sx={{ 
        borderRadius: 4,
        position: 'relative',
        zIndex: 1,
        background: isDarkMode 
          ? 'linear-gradient(145deg, rgba(30,30,35,0.95) 0%, rgba(25,25,30,0.98) 100%)'
          : 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(250,250,252,0.98) 100%)',
        backdropFilter: 'blur(20px)',
        boxShadow: isDarkMode
          ? '0 20px 60px rgba(0,0,0,0.5), 0 0 1px rgba(255,255,255,0.1) inset'
          : '0 20px 60px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.05) inset',
      }}>
        <CardHeader
          title={
            <Typography 
              variant="h5" 
              fontWeight={700}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Patients
            </Typography>
          }
          subheader="Create, search, edit and delete patient records"
          action={
            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh">
                <IconButton onClick={() => fetchPatients(q)} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
        <CardContent>
          <Stack spacing={2}>
            <TextField
              placeholder="Search by patient ID, given/family name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchPatients(q, true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: q ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setQ("");
                        fetchPatients("", true);
                      }}
                      aria-label="clear search"
                    >
                      <CloseIcon />
                    </IconButton>
                    <Button onClick={() => fetchPatients(q, true)} disabled={loading}>
                      Search
                    </Button>
                  </InputAdornment>
                ) : (
                  <Button onClick={() => fetchPatients(q, true)} disabled={loading}>
                    Search
                  </Button>
                ),
              }}
              data-tour="patient-search"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                  },
                  '&.Mui-focused': {
                    bgcolor: theme.palette.background.paper,
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                  }
                }
              }}
            />
            
            {/* Unified Card View for Mobile and Desktop */}
            <Stack spacing={isMobile ? 2 : 3}>
              {rows.map((r) => {
                const isOpen = expandedPatients.has(r.patientId);
                const sessions = patientSessions[r.patientId] || [];
                const isLoadingSessions = loadingSessions.has(r.patientId);
                const age = r.dateOfBirth ? calculateAge(r.dateOfBirth) : null;

                return (
                  <Card
                    key={r.id}
                    elevation={isMobile ? 2 : 3}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: isMobile ? 3 : 4,
                      background: alpha(theme.palette.background.paper, 0.6),
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: isMobile ? 'none' : 'translateY(-2px)',
                        boxShadow: isMobile ? undefined : `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: isMobile ? 2 : 3, '&:last-child': { pb: isMobile ? 2 : 3 } }}>
                      {/* Header Row */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: isMobile ? 2 : 3,
                          mb: isMobile ? 1.5 : 2
                        }}
                        onClick={isMobile ? () => openActionSheet(r) : undefined}
                      >
                        <Avatar
                          sx={{
                            width: isMobile ? 48 : 64,
                            height: isMobile ? 48 : 64,
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            color: theme.palette.primary.main,
                            fontWeight: 700,
                            fontSize: isMobile ? '1.1rem' : '1.5rem',
                          }}
                        >
                          {r.givenName?.[0]}{r.familyName?.[0]}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant={isMobile ? "h6" : "h5"}
                            sx={{ 
                              fontWeight: 700,
                              fontSize: isMobile ? '1.1rem' : '1.5rem',
                              mb: isMobile ? 0.5 : 1,
                              lineHeight: 1.2
                            }}
                          >
                            {r.givenName} {r.familyName}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" gap={isMobile ? 0.5 : 1}>
                            {age && (
                              <Chip 
                                icon={<CakeIcon />}
                                label={age}
                                size={isMobile ? "small" : "medium"}
                                variant="outlined"
                                sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                              />
                            )}
                            {r.sex && (
                              <Chip 
                                icon={<WcIcon />}
                                label={r.sex}
                                size={isMobile ? "small" : "medium"}
                                variant="outlined"
                                sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                              />
                            )}
                            {sessions.length > 0 && (
                              <Chip 
                                icon={<FolderIcon />}
                                label={`${sessions.length} session${sessions.length !== 1 ? 's' : ''}`}
                                size={isMobile ? "small" : "medium"}
                                color="primary"
                                variant="filled"
                                sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                              />
                            )}
                            {r.createdAt && !isMobile && (
                              <Chip 
                                label={dayjs(r.createdAt).format("MMM D, YYYY")}
                                size="medium"
                                variant="outlined"
                                sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {!isMobile && (
                            <Box sx={{ display: 'flex', gap: 0.5 }} data-tour={r === rows[0] ? "patient-actions" : undefined}>
                              <Tooltip title="Mole Mapping" arrow>
                                <IconButton
                                  onClick={() => goToMoleMapping(r.patientId)}
                                  size="medium"
                                  sx={{
                                    color: theme.palette.success.main,
                                    bgcolor: alpha(theme.palette.success.main, 0.08),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.success.main, 0.15),
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <MapIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit" arrow>
                                <IconButton 
                                  onClick={() => openEditDialog(r)}
                                  size="medium"
                                  sx={{
                                    color: theme.palette.warning.main,
                                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete" arrow>
                                <IconButton
                                  onClick={() => openDeleteDialog(r)}
                                  size="medium"
                                  sx={{
                                    color: theme.palette.error.main,
                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.error.main, 0.15),
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePatientExpansion(r.patientId);
                            }}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                              }
                            }}
                          >
                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Patient ID */}
                      <Chip
                        label={`ID: ${r.patientId.substring(0, isMobile ? 12 : 20)}...`}
                        size={isMobile ? "small" : "medium"}
                        variant="outlined"
                        icon={<ContentCopyIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyId(r.patientId, 'patient');
                        }}
                        sx={{ 
                          fontFamily: 'monospace',
                          fontSize: isMobile ? '0.7rem' : '0.875rem',
                          height: isMobile ? 24 : 32,
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiChip-icon': {
                            fontSize: isMobile ? '0.75rem' : '1rem',
                          }
                        }}
                      />

                      {/* Expanded Sessions */}
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: isMobile ? 2 : 3, pt: isMobile ? 2 : 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                          <Typography variant={isMobile ? "subtitle2" : "h6"} fontWeight={600} gutterBottom>
                            Mole Mapping Sessions
                          </Typography>
                          
                          {isLoadingSessions ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: isMobile ? 2 : 3 }}>
                              <CircularProgress size={isMobile ? 24 : 32} />
                            </Box>
                          ) : sessions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: isMobile ? 2 : 3, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                              No mole mapping sessions yet.
                            </Typography>
                          ) : (
                            <Stack spacing={isMobile ? 1 : 1.5}>
                              {sessions.map((session) => (
                                <Card
                                  key={session.id}
                                  variant="outlined"
                                  sx={{
                                    borderRadius: isMobile ? 2 : 3,
                                    bgcolor: alpha(theme.palette.background.default, 0.5),
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.background.default, 0.7),
                                      borderColor: theme.palette.primary.main,
                                    }
                                  }}
                                >
                                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={isMobile ? 1 : 2}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant={isMobile ? "body2" : "body1"} fontWeight={600} sx={{ mb: 0.5 }}>
                                          {dayjs(session.sessionDate).format('MMMM D, YYYY')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: session.notes ? 0.5 : 0 }}>
                                          Created: {dayjs(session.createdAt).format('MMM D, YYYY h:mm A')}
                                        </Typography>
                                        {session.notes && (
                                          <Typography variant="body2" sx={{ fontSize: isMobile ? '0.825rem' : '0.95rem', color: 'text.secondary', mt: 0.5 }}>
                                            {session.notes}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: isMobile ? 0 : 0.5 }}>
                                        <Tooltip title="View Session" arrow>
                                          <IconButton
                                            size={isMobile ? "small" : "medium"}
                                            onClick={() => goToMoleMapping(r.patientId)}
                                            sx={{
                                              color: theme.palette.info.main,
                                              bgcolor: isMobile ? 'transparent' : alpha(theme.palette.info.main, 0.08),
                                              '&:hover': {
                                                bgcolor: isMobile ? alpha(theme.palette.info.main, 0.08) : alpha(theme.palette.info.main, 0.15),
                                                transform: 'scale(1.1)',
                                              },
                                              transition: 'all 0.2s',
                                            }}
                                          >
                                            <MapIcon fontSize={isMobile ? "small" : "medium"} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}

              {!loading && rows.length === 0 && (
                <Box sx={{ textAlign: 'center', py: isMobile ? 6 : 8 }}>
                  <PersonIcon sx={{ fontSize: isMobile ? 64 : 96, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary" variant={isMobile ? "body1" : "h6"}>
                    No patients yet.
                  </Typography>
                </Box>
              )}
              
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: isMobile ? 4 : 6 }}>
                  <CircularProgress size={isMobile ? 40 : 48} />
                </Box>
              )}
            </Stack>
            
            {/* Load More Patients */}
            {hasMore && !loading && (
              <Button
                onClick={() => fetchPatients()}
                variant="outlined"
                size="large"
                fullWidth={isMobile}
                sx={{
                  mt: 3,
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Load More Patients
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Patient Form Dialog */}
      <PatientFormDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleCreate}
      />

      {/* Edit Patient Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Patient
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Given Name"
              value={editForm.givenName}
              onChange={(e) => setEditForm({ ...editForm, givenName: e.target.value })}
              error={!!editErrors.givenName}
              helperText={editErrors.givenName}
              fullWidth
            />
            <TextField
              label="Family Name"
              value={editForm.familyName}
              onChange={(e) => setEditForm({ ...editForm, familyName: e.target.value })}
              error={!!editErrors.familyName}
              helperText={editErrors.familyName}
              fullWidth
            />
            <TextField
              label="Date of Birth"
              type="date"
              value={editForm.dateOfBirth}
              onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
            <TextField
              label="Sex"
              select
              value={editForm.sex}
              onChange={(e) => setEditForm({ ...editForm, sex: e.target.value })}
              fullWidth
            >
              {sexes.map((sex) => (
                <MenuItem key={sex} value={sex}>
                  {sex.charAt(0).toUpperCase() + sex.slice(1)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes"
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              multiline
              rows={4}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Session Dialog */}
      <Dialog
        open={editSessionOpen}
        onClose={() => setEditSessionOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Mole Mapping Session
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Session Date"
              type="date"
              value={sessionForm.sessionDate}
              onChange={(e) => setSessionForm({ ...sessionForm, sessionDate: e.target.value })}
              error={!!sessionErrors.sessionDate}
              helperText={sessionErrors.sessionDate}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
            <TextField
              label="Notes"
              value={sessionForm.notes}
              onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })}
              multiline
              rows={4}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSessionOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleUpdateSession} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tour Guide */}
      <TourGuide
        steps={tourSteps}
        tourId="patient-space"
        run={runTour}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        onFinish={handleFinishTour}
        onSkip={handleFinishTour}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete Patient</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Are you sure you want to delete this patient?
            </Alert>
            {patientToDelete && (
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {patientToDelete.givenName} {patientToDelete.familyName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Patient ID: {patientToDelete.patientId}
                </Typography>
                {patientToDelete.dateOfBirth && (
                  <Typography variant="body2" color="text.secondary">
                    DOB: {dayjs(patientToDelete.dateOfBirth).format("YYYY-MM-DD")}
                  </Typography>
                )}
              </Box>
            )}
            <Typography variant="body2" color="error">
              This action cannot be undone. All patient data and associated mole mapping sessions will be permanently deleted.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => patientToDelete && handleDelete(patientToDelete.id)}
          >
            Delete Patient
          </Button>
        </DialogActions>
      </Dialog>

      {/* Patient Actions Bottom Sheet (Mobile) */}
      <Drawer
        anchor="bottom"
        open={actionSheetOpen}
        onClose={closeActionSheet}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '70vh',
            pb: 3,
          }
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
          {/* Drag Handle */}
          <Box
            sx={{
              height: 4,
              width: 40,
              bgcolor: 'grey.300',
              borderRadius: 2,
              mx: 'auto',
              mt: 1.5,
              mb: 2,
            }}
          />
          
          {selectedPatient && (
            <>
              {/* Patient Info Header */}
              <Box sx={{ px: 3, pb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      fontSize: '1.25rem',
                    }}
                  >
                    {selectedPatient.givenName?.[0]}{selectedPatient.familyName?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {selectedPatient.givenName} {selectedPatient.familyName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {selectedPatient.patientId.substring(0, 16)}...
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Actions List */}
              <List sx={{ px: 1 }}>
                <ListItemButton
                  onClick={() => handleActionAndClose(() => goToMoleMapping(selectedPatient.patientId))}
                  data-tour="patient-actions"
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                    }
                  }}
                >
                  <ListItemIcon>
                    <MapIcon sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Mole Mapping"
                    secondary="Navigate to mole mapping"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>

                <ListItemButton
                  onClick={() => handleActionAndClose(() => openEditDialog(selectedPatient))}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    }
                  }}
                >
                  <ListItemIcon>
                    <EditIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Edit Patient"
                    secondary="Update patient information"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>

                <ListItemButton
                  onClick={() => handleActionAndClose(() => handleCopyId(selectedPatient.patientId, 'patient'))}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.info.main, 0.08),
                    }
                  }}
                >
                  <ListItemIcon>
                    <ContentCopyIcon sx={{ color: theme.palette.info.main }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Copy Patient ID"
                    secondary="Copy full ID to clipboard"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>

                <Divider sx={{ my: 1 }} />

                <ListItemButton
                  onClick={() => handleActionAndClose(() => openDeleteDialog(selectedPatient))}
                  sx={{
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.error.main, 0.08),
                    }
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon sx={{ color: theme.palette.error.main }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Delete Patient"
                    secondary="Permanently remove patient record"
                    primaryTypographyProps={{ fontWeight: 600, color: 'error.main' }}
                  />
                </ListItemButton>
              </List>
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
